import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import moment from "moment";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Modern DateRangePicker component - replacement for react-bootstrap-daterangepicker
 * Uses shadcn components and moment.js for compatibility with existing code
 * 
 * @param {string} displayMode - 'unified' (default) or 'button'
 */
export default function DateRangePicker({ 
  onApply, 
  onCancel,
  startDate,
  endDate,
  children,
  containerStyles = {},
  containerClass = "",
  autoUpdateInput = false,
  displayMode = "button",
  locale = {
    format: 'MM/DD/YYYY',
    applyLabel: 'Apply',
    cancelLabel: 'Cancel',
    customRangeLabel: 'Custom Range',
  },
  ...props 
}) {
  const [date, setDate] = useState({
    from: startDate ? moment(startDate).toDate() : undefined,
    to: endDate ? moment(endDate).toDate() : undefined,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Update internal state when props change (only if user hasn't interacted)
  useEffect(() => {
    if (!hasInteracted) {
      setDate({
        from: startDate ? moment(startDate).toDate() : undefined,
        to: endDate ? moment(endDate).toDate() : undefined,
      });
    }
  }, [startDate, endDate, hasInteracted]);

  const handleSelect = (selectedRange) => {
    // Don't do anything if selectedRange is undefined or null (happens during month navigation)
    if (!selectedRange) {
      return;
    }
    
    // Check if this is an actual user interaction (not just the same range being passed back)
    const isSameRange = 
      selectedRange?.from?.getTime() === date?.from?.getTime() &&
      selectedRange?.to?.getTime() === date?.to?.getTime();
    
    if (isSameRange) {
      // User clicked on already selected range or navigation happened
      return;
    }
    
    // Mark that user has interacted
    setHasInteracted(true);
    
    // If both dates are already selected (complete range),
    // clear the selection and start fresh with the newly clicked date
    if (date?.from && date?.to) {
      // Start a new selection
      setDate({ from: selectedRange?.from || selectedRange, to: undefined });
    } else {
      // Continue building the range
      setDate(selectedRange);
    }
  };

  const handleApply = () => {
    if (date?.from && date?.to) {
      // Call onApply with moment objects for compatibility
      const event = {
        target: {
          value: `${moment(date.from).format(locale.format)} - ${moment(date.to).format(locale.format)}`
        }
      };
      
      const picker = {
        startDate: moment(date.from),
        endDate: moment(date.to),
      };

      if (onApply) {
        onApply(event, picker);
      }
      
      setHasInteracted(false);
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    setDate({
      from: startDate ? moment(startDate).toDate() : undefined,
      to: endDate ? moment(endDate).toDate() : undefined,
    });
    
    setHasInteracted(false);
    
    if (onCancel) {
      onCancel();
    }
    
    setIsOpen(false);
  };

  const displayText = date?.from && date?.to
    ? `${moment(date.from).format(locale.format)} - ${moment(date.to).format(locale.format)}`
    : children || "Pick a date range";

  return (
    <div className={cn("grid gap-2", containerClass)} style={containerStyles}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          {displayMode === "unified" ? (
            <div 
              className="flex items-stretch border border-gray-300 rounded bg-white shadow-sm hover:border-brand-accent transition-colors cursor-pointer w-full sm:w-[330px] h-10"
            >
              {/* From Section */}
              <div className="flex-1 flex items-center gap-2 px-4 py-2 relative">
                <span className="text-sm text-gray-500 font-normal">From</span>
                <span className="text-sm font-semibold text-brand-dark">
                  {date?.from ? moment(date.from).format(locale.format) : 'Select'}
                </span>
                <ChevronDown className="h-3 w-3 text-brand-dark ml-auto" />
              </div>
              
              {/* Divider */}
              <div className="w-px bg-gray-300"></div>
              
              {/* To Section */}
              <div className="flex-1 flex items-center gap-2 px-4 py-2 relative">
                <span className="text-sm text-gray-500 font-normal">To</span>
                <span className="text-sm font-semibold text-brand-dark">
                  {date?.to ? moment(date.to).format(locale.format) : 'Select'}
                </span>
                <ChevronDown className="h-3 w-3 text-brand-dark ml-auto" />
              </div>
            </div>
          ) : children ? (
            <div>{children}</div>
          ) : (
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-normal",
                !date && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {displayText}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={handleSelect}
            numberOfMonths={2}
            disabled={(date) => date > new Date()}
            toMonth={new Date()}
            compact
            classNames={{
              day: cn(
                "h-7 w-7 p-0 font-normal aria-selected:opacity-100",
                "[&[data-range-start=true]]:rounded-l-md [&[data-range-start=true]]:rounded-r-none [&[data-range-start=true]]:bg-accent",
                "[&[data-range-end=true]]:rounded-r-md [&[data-range-end=true]]:rounded-l-none [&[data-range-end=true]]:bg-accent",
                "[&[data-range-middle=true]]:rounded-none [&[data-range-middle=true]]:bg-brand-active-bg"
              ),
              day_button: cn(
                "h-7 w-7 text-foreground",
                "data-[range-start=true]:rounded-l-md data-[range-start=true]:rounded-r-none",
                "data-[range-end=true]:rounded-r-md data-[range-end=true]:rounded-l-none",
                "data-[range-middle=true]:rounded-none",
                "data-[range-start=true]:bg-accent data-[range-start=true]:text-white data-[range-start=true]:hover:bg-accent data-[range-start=true]:hover:text-white",
                "data-[range-end=true]:bg-accent data-[range-end=true]:text-white data-[range-end=true]:hover:bg-accent data-[range-end=true]:hover:text-white",
                "data-[range-middle=true]:bg-brand-active-bg data-[range-middle=true]:text-foreground",
                "data-[selected-single=true]:bg-accent data-[selected-single=true]:text-white"
              ),
            }}
          />
          <div className="flex items-center justify-end gap-2 p-2 border-t">
            <Button variant="default" size="sm" onClick={handleCancel}>
              {locale.cancelLabel}
            </Button>
            <Button 
              variant="secondary"
              size="sm" 
              onClick={handleApply}
              disabled={!date?.from || !date?.to}
            >
              {locale.applyLabel}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
